import React, { useEffect } from 'react';
import { Button } from '../components/Button';
import { AuthLayout } from '../components/AuthLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

function ServiceSelection() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	// Handle tokens from URL query parameters and save globally
	// This only runs if tokens are in the URL (e.g., from external redirects)
	// Normal login tokens are already saved in Login.js before redirecting here
	useEffect(() => {
		const token = searchParams.get('token');
		const refreshToken = searchParams.get('refresh_token');
		
		// Only process if tokens exist in URL (from external redirects)
		// This won't interfere with normal login since normal login doesn't include tokens in URL
		if (token) {
			localStorage.setItem('access_token', token);
			// Set axios default header globally for all API calls
			axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
		}

		if (refreshToken) {
			localStorage.setItem('refresh_token', refreshToken);
		}

		// Clean up URL by removing token params (optional, for security)
		if (token || refreshToken) {
			const newSearchParams = new URLSearchParams(window.location.search);
			newSearchParams.delete('token');
			newSearchParams.delete('refresh_token');
			const newSearch = newSearchParams.toString();
			const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
			window.history.replaceState({}, '', newUrl);
		}
	}, [searchParams]);

	// If support query param exists, redirect directly to continuation action selection
	useEffect(() => {
		if (searchParams.get('support') !== null || window.location.search === '?support') {
			navigate('/selfservice/continuation?support');
		}
	}, [navigate, searchParams]);

	const handleServiceChoice = (serviceType) => {
		if (serviceType === 'synthetic') {
			navigate('/selfservice/synthetic');
		} else if (serviceType === 'continuation') {
			navigate('/selfservice/continuation');
		}
	};
        

	return (
		<AuthLayout title="Choose Your Service">
			<div className="space-y-6">
				<p className="text-center text-gray-600 mb-8">
					Select the type of service you'd like to access
				</p>
				
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					{/* Self Service Card - Synthetic Signals */}
					<div 
						onClick={() => handleServiceChoice('synthetic')}
						className="group relative cursor-pointer rounded-xl border-2 border-gray-200 bg-white p-8 shadow-lg transition-all duration-300 hover:border-[#28a745] hover:shadow-xl hover:scale-105"
					>
						<div className="flex flex-col items-center text-center">
							<div className="mb-4 rounded-full bg-[#28a745]/10 p-4 group-hover:bg-[#28a745]/20 transition-colors">
								<svg 
									className="h-12 w-12 text-[#28a745]" 
									fill="none" 
									stroke="currentColor" 
									viewBox="0 0 24 24"
								>
									<path 
										strokeLinecap="round" 
										strokeLinejoin="round" 
										strokeWidth={2} 
										d="M13 10V3L4 14h7v7l9-11h-7z" 
									/>
								</svg>
							</div>
							<h3 className="text-xl font-semibold text-gray-900 mb-2">
								Self Service
							</h3>
							<p className="text-sm text-gray-600 mb-6">
								Synthetic Signals & Synthetic Signals Lite
							</p>
							<Button 
								color="cyan" 
								className="w-full duration-200 hover:bg-gray-800 focus:bg-[#28a745] focus:text-white/80"
							>
								Select
							</Button>
						</div>
					</div>

					{/* Continuation Card - Other Options */}
					<div 
						onClick={() => handleServiceChoice('continuation')}
						className="group relative cursor-pointer rounded-xl border-2 border-gray-200 bg-white p-8 shadow-lg transition-all duration-300 hover:border-[#28a745] hover:shadow-xl hover:scale-105"
					>
						<div className="flex flex-col items-center text-center">
							<div className="mb-4 rounded-full bg-[#28a745]/10 p-4 group-hover:bg-[#28a745]/20 transition-colors">
								<svg 
									className="h-12 w-12 text-[#28a745]" 
									fill="none" 
									stroke="currentColor" 
									viewBox="0 0 24 24"
								>
									<path 
										strokeLinecap="round" 
										strokeLinejoin="round" 
										strokeWidth={2} 
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
									/>
								</svg>
							</div>
							<h3 className="text-xl font-semibold text-gray-900 mb-2">
								Continuation
							</h3>
							<p className="text-sm text-gray-600 mb-6">
								Account Continuation, Second Chance & Aso
							</p>
							<Button 
								color="cyan" 
								className="w-full duration-200 hover:bg-gray-800 focus:bg-[#28a745] focus:text-white/80"
							>
								Select
							</Button>
						</div>
					</div>
				</div>
			</div>
		</AuthLayout>
	);
}

export default ServiceSelection;

